import React, { useEffect, useState } from 'react';
import './contactUsForm.css';
import CenterStage from '../../Components/CenterStage/CenterStage';
import TwoColumnPoints from '../../Components/TwoColumnPoints/TwoColumnPoints';
import SectionHeader from '../../Components/SectionHeader/SectionHeader';
import TextCaption from '../../Components/TextCaption/TextCaption';
import imgMHSBrazil from '../../assets/BrazilServicos/MHSBrazil.jpeg';
import { FaBuilding, FaEnvelope, FaExclamationCircle, FaMapMarkerAlt, FaPhone, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import WhatsAppChat from '../../Components/WhatsAppChat/WhatsAppChat';
import FeatureCardCarousel from '../../Components/FeaturesCardCarousal/FeatureCardCarousal';
import imgMark from '../../assets/mark.png';
import ThreeDImageCarousel from '../../Components/CircleCarousel/CircleCarousel';
import imgIconAssembly from '../../assets/BrazilServicos/miningCampaign/Brazil campaign icons_Assembly of custom hydraulic assemblies.png';
import imgIconEmergency from '../../assets/BrazilServicos/miningCampaign/Brazil campaign icons_Emergency hydraulic service.png';
import imgIconHoseReplacement from '../../assets/BrazilServicos/miningCampaign/Brazil campaign icons_Hydraulic hose replacement.png';
import imgIconMobileUnits from '../../assets/BrazilServicos/miningCampaign/Brazil campaign icons_Mobile hydraulic service units.png';
import imgIconPreventive from '../../assets/BrazilServicos/miningCampaign/Brazil campaign icons_Preventive hydraulic maintenance.png';

import imgTrucks from '../../assets/BrazilServicos/miningCampaign/trucks.webp';
import imgExcavator from '../../assets/BrazilServicos/miningCampaign/excavator.jpg';
import imgCrushers from '../../assets/BrazilServicos/miningCampaign/crusher.jpg';
import imgDrillingEquipment from '../../assets/BrazilServicos/miningCampaign/drilling.jpg';
import imgMineralProcessingEquipment from '../../assets/BrazilServicos/miningCampaign/Mineral_processing_equipment.webp';
import imgIndustrialHydraulicSystems from '../../assets/BrazilServicos/miningCampaign/Industrial_hydraulic_systems.webp';
import ShowcaseCarousel from '../../Components/ShowcaseCarousel/ShowcaseCarousel';


const MiningCampaignMG = () => {
  const SOURCE = 'mining-campaign-mg';
  const MAX_MESSAGE_LENGTH = 1000;
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on component mount
}, []);

  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    telephone: '',
    cityState: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const formatTelephone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const validateField = (name, value) => {
    const v = (value ?? '').toString();
    let error = '';

    switch (name) {
      case 'fullName':
        if (!v.trim()) error = 'Full name is required';
        break;
      case 'company':
        if (!v.trim()) error = 'Company is required';
        break;
      case 'email':
        if (!v.trim()) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) error = 'Please enter a valid email address';
        break;
      case 'telephone':
        if (!v.trim()) error = 'Telephone is required';
        else if (v.replace(/\D/g, '').length < 10) error = 'Please enter a valid telephone number';
        break;
      case 'cityState':
        if (!v.trim()) error = 'City / State is required';
        break;
      case 'message':
        if (!v.trim()) error = 'Message is required';
        else if (v.trim().length < 10) error = 'Message must be at least 10 characters';
        else if (v.length > MAX_MESSAGE_LENGTH) error = `Message must not exceed ${MAX_MESSAGE_LENGTH} characters`;
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const processedValue = name === 'telephone' ? formatTelephone(value) : value;

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, processedValue) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateForm = () => {
    const requiredFields = ['fullName', 'company', 'email', 'telephone', 'cityState', 'message'];
    const newErrors = {};
    const newTouched = {};

    requiredFields.forEach((key) => {
      newTouched[key] = true;
      const err = validateField(key, formData[key]);
      if (err) newErrors[key] = err;
    });

    setTouched((prev) => ({ ...prev, ...newTouched }));
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      company: '',
      email: '',
      telephone: '',
      cityState: '',
      message: ''
    });
    setTouched({});
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitSuccess(false);
    setErrors((prev) => ({ ...prev, submit: '' }));

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.hansa-flex.co.in';
      const API_URL = `${API_BASE}/api/hf-brazil/contact/${SOURCE}`;
      const composedMessage = `Campaign: Mining MG\nCompany: ${formData.company}\nCity/State: ${formData.cityState}\n\n${formData.message}`;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: SOURCE,
          fullName: formData.fullName,
          company: formData.company,
          email: formData.email,
          telephone: formData.telephone,
          cityState: formData.cityState,
          cpfCnpj: '',
          message: composedMessage
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to send');

      setSubmitSuccess(true);
      setTimeout(() => {
        resetForm();
        setSubmitSuccess(false);
        setIsSubmitting(false);
      }, 5000);
    } catch (error) {
      setErrors((prev) => ({ ...prev, submit: error.message || 'Failed to send. Please try again later.' }));
      setIsSubmitting(false);
    }
  };

  const listItems = [
    'Hydraulic hose replacement',
    'Preventive maintenance',
    'Emergency service'
  ];

  const featuresData = [
    {
      title: 'Hydraulic hose replacement',
      description: 'Quick and safe replacement of hoses to restore equipment operation.',
      image: imgIconHoseReplacement
    },
    {
      title: 'Preventive hydraulic maintenance',
      description: 'Scheduled inspections to identify wear and prevent failures.',
      image: imgIconPreventive
    },
    {
      title: 'Emergency hydraulic service',
      description: 'Technical support to minimize unexpected downtime.',
      image: imgIconEmergency
    },
    // {
    //   title: 'Mobile hydraulic service units',
    //   description: 'Vehicles equipped for direct on-site service.',
    //   image: imgIconMobileUnits
    // },
    {
      title: 'Assembly of custom hydraulic assemblies',
      description: 'Hoses and components developed for demanding industrial applications.',
      image: imgIconAssembly
    }
  ];

  const equipmentServicedData = [
    {
      image: imgTrucks,
      title: 'Trucks',
    },
    {
      image: imgExcavator,
      title: 'Excavators',
    },
    {
      image: imgCrushers,
      title: 'Crushers',
    },
    {
      image: imgDrillingEquipment,
      title: 'Drilling Equipment',
    },
    {
      image: imgMineralProcessingEquipment,
      title: 'Mineral processing equipment',
    },
    {
      image: imgIndustrialHydraulicSystems,
      title: 'Industrial hydraulic systems',
    }
  ];

  const equipmentServicedData2 = [
    {
      imgSrc: imgTrucks,
      title: 'Trucks',
    },
    {
      imgSrc: imgExcavator,
      title: 'Excavators',
    },
    {
      imgSrc: imgCrushers,
      title: 'Crushers',
    },
    {
      imgSrc: imgDrillingEquipment,
      title: 'Drilling Equipment',
    },
    {
      imgSrc: imgMineralProcessingEquipment,
      title: 'Mineral processing equipment',
    },
    {
      imgSrc: imgIndustrialHydraulicSystems,
      title: 'Industrial hydraulic systems',
    }
  ];

  const advantagesData = [
    {
      title: 'Reduced downtime',
      description: 'Fast and efficient services to keep operations running.',
      image: imgMark
    },
    {
      title: 'High-quality hydraulic components',
      description: 'Hoses and fittings designed for harsh industrial environments.',
      image: imgMark
    },
    {
      title: 'Specialized technical team',
      description: 'Professionals trained to work on complex hydraulic systems.',
      image: imgMark
    },
    {
      title: 'Safety and reliability',
      description: 'Services performed in accordance with international technical standards.',
      image: imgMark
    },
    {
      title: 'Global experience in hydraulic solutions',
      description: 'Know-how applied across various industrial sectors worldwide.',
      image: imgMark
    }
  ];

  return (
    <div>
      <CenterStage
        imageSrc={imgMHSBrazil}
        title={'Hydraulic Solutions for Mining in Minas Gerais'}
        paragraph={'Specialized services in hydraulic hoses, maintenance, and technical support for mining operations.'}
      />

      <TwoColumnPoints points={listItems} />

      <SectionHeader title={"ABOUT HANSA-FLEX"} />
      <br />
      <TextCaption paragraph={'HANSA-FLEX is one of the leading global suppliers of hydraulic solutions, offering comprehensive maintenance services, hose replacement, and technical support for industrial hydraulic systems.'} />
      <TextCaption paragraph={'With extensive experience in demanding industrial environments, the company supports operations that require high reliability, operational safety, and rapid technical response.'} />
      <br /><br />

      <div>
        <SectionHeader
          title={'SERVICES FOR MINING OPERATIONS'}
        />
        <FeatureCardCarousel featuresData={featuresData} />
      </div>

      <div>
        <SectionHeader title={"EQUIPMENT SERVICED"} />
        {/* <ShowcaseCarousel carouselItems={equipmentServicedData2} isExploreButton={false} descDisplay='block' carouselType={2} /> */}
        <ThreeDImageCarousel items={equipmentServicedData} />
      </div>

      <div>
        <SectionHeader
          title={'BENEFITS FOR MINING COMPANIES'}
        />
        <FeatureCardCarousel featuresData={advantagesData} />
      </div>


      <div className='contact-form-container'>
        <h1 className="contact-form-title">{t('Mining Contact Form (MG)')}</h1>
        <p className="contact-form-instruction">{t('Contact our team and request specialized service for the maintenance or replacement of hydraulic hoses.')}</p>
        <form className="contact-form" onSubmit={handleSubmit} style={{ pointerEvents: isSubmitting ? 'none' : 'auto', opacity: isSubmitting ? 0.7 : 1 }}>
          <div className="form-group">
            <div className="input-wrapper">
              <input type="text" name="fullName" placeholder={t('Name*')} value={formData.fullName} onChange={handleInputChange} onBlur={handleBlur} className={`form-input ${errors.fullName ? 'input-error' : ''}`} disabled={isSubmitting} />
              <FaUser className="input-icon" />
            </div>
            {errors.fullName && <span className="error-message"><FaExclamationCircle className="error-icon" />{t(errors.fullName)}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input type="text" name="company" placeholder={t('Company*')} value={formData.company} onChange={handleInputChange} onBlur={handleBlur} className={`form-input ${errors.company ? 'input-error' : ''}`} disabled={isSubmitting} />
              <FaBuilding className="input-icon" />
            </div>
            {errors.company && <span className="error-message"><FaExclamationCircle className="error-icon" />{t(errors.company)}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input type="email" name="email" placeholder={t('Email*')} value={formData.email} onChange={handleInputChange} onBlur={handleBlur} className={`form-input ${errors.email ? 'input-error' : ''}`} disabled={isSubmitting} />
              <FaEnvelope className="input-icon" />
            </div>
            {errors.email && <span className="error-message"><FaExclamationCircle className="error-icon" />{t(errors.email)}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input type="tel" name="telephone" placeholder={t('Phone*')} value={formData.telephone} onChange={handleInputChange} onBlur={handleBlur} className={`form-input ${errors.telephone ? 'input-error' : ''}`} disabled={isSubmitting} />
              <FaPhone className="input-icon" />
            </div>
            {errors.telephone && <span className="error-message"><FaExclamationCircle className="error-icon" />{t(errors.telephone)}</span>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input type="text" name="cityState" placeholder={t('City / State*')} value={formData.cityState} onChange={handleInputChange} onBlur={handleBlur} className={`form-input ${errors.cityState ? 'input-error' : ''}`} disabled={isSubmitting} />
              <FaMapMarkerAlt className="input-icon" />
            </div>
            {errors.cityState && <span className="error-message"><FaExclamationCircle className="error-icon" />{t(errors.cityState)}</span>}
          </div>

          <div className="form-group message-group">
            <textarea name="message" placeholder={t('Message*')} value={formData.message} onChange={handleInputChange} onBlur={handleBlur} maxLength={MAX_MESSAGE_LENGTH} className={`form-textarea ${errors.message ? 'input-error' : ''}`} rows="6" disabled={isSubmitting} />
            {errors.message && <span className="error-message"><FaExclamationCircle className="error-icon" />{t(errors.message)}</span>}
          </div>

          {errors.submit && <div className="error-message" style={{ marginTop: '10px', marginBottom: '10px' }}><FaExclamationCircle className="error-icon" />{t(errors.submit)}</div>}
          {submitSuccess && <div style={{ marginTop: '10px', marginBottom: '10px', color: 'green' }}>{t('Submitted successfully. Please check your email.')}</div>}

          <button type="submit" className={`submit-button ${isSubmitting ? 'submitting' : ''}`} disabled={isSubmitting}>
            {isSubmitting ? t('Sending...') : t('Send')}
          </button>
        </form>
      </div>

      <br /><br />
      <WhatsAppChat phoneNumber="+55 (31) 99236-0047" />
    </div>
  );
};

export default MiningCampaignMG;
